import React, { useState, useEffect, useRef, useContext } from 'react';
import { FaSearch, FaCalendarAlt } from 'react-icons/fa';
import { FiPlus, FiEdit, FiTrash2, FiSmile } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import routesConfig, { getTranslation } from '../../../Config/sidebarConfig'; // Adjust path if needed
import './css/searchBar.css';
import useApi from '../../../Hooks/useApi';
import useTranslations from '../../../Hooks/useTranslations';
import useLanguage from '../../../Hooks/useLanguage';
import translations from './translations.json';
import configTranslations from '../../../Config/translations.json';
import { SearchContext } from '../../../Context/SearchContext.js';

const SearchBar = ({ value: externalValue, onChange: externalOnChange }) => {
  const api = useApi();
  const t = useTranslations(translations);
  const preferredLanguage = useLanguage();
  const [searchValue, setSearchValue] = useState(externalValue || '');
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [emailNotifications, setEmailNotifications] = useState([]);
  const navigate = useNavigate();
  const searchRef = useRef(null);
  const { setReservationSearchQuery } = useContext(SearchContext);

  // Sync internal state with external value
  useEffect(() => {
    if (externalValue !== undefined) {
      setSearchValue(externalValue);
    }
  }, [externalValue]);

  // React to changes in the input
  useEffect(() => {
    const term = searchValue.trim();
    if (term === '') {
      setFilteredRoutes([]);
      setEmailNotifications([]);
      return;
    }

    let isCancelled = false;
    const abortController = new AbortController();

    const isValidEmail = /\S+@\S+\.\S+/.test(term);
    if (isValidEmail) {
      (async () => {
        try {
          const data = await api.get(
            `${window.baseDomain}api/notifications/email/${encodeURIComponent(term)}`,
            { noCache: true, signal: abortController.signal }
          );
          if (isCancelled) return;
          if (Array.isArray(data)) {
            const sorted = data
              .sort((a, b) => new Date(b.creationTimestamp) - new Date(a.creationTimestamp))
              .slice(0, 100);
            setEmailNotifications(sorted);
          } else {
            setEmailNotifications([]);
          }
        } catch (err) {
          if (isCancelled) return;
          console.error('Error fetching notifications:', err);
          setEmailNotifications([]);
        }
      })();
      setFilteredRoutes([]);
    } else {
      const langTranslations = configTranslations[preferredLanguage] || configTranslations['nl'];
      const results = routesConfig.filter(route => {
        const label = getTranslation(langTranslations, route.labelKey, '');
        return label && label.toLowerCase().includes(term.toLowerCase());
      });
      setFilteredRoutes(results);
      setEmailNotifications([]);
    }

    return () => {
      isCancelled = true;
      abortController.abort();
    };
  }, [searchValue, api]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = e => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setFilteredRoutes([]);
        setEmailNotifications([]);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = e => {
    setSearchValue(e.target.value);
    externalOnChange && externalOnChange(e);
  };

  const handleResultClick = path => {
    setSearchValue('');
    setFilteredRoutes([]);
    setEmailNotifications([]);
    if (externalOnChange) {
      externalOnChange({ target: { value: '' } });
    }
    navigate(path);
  };

  const handleReservationSearchClick = () => {
    const query = searchValue.trim();
    setReservationSearchQuery(query);
    setSearchValue('');
    setFilteredRoutes([]);
    setEmailNotifications([]);
    if (externalOnChange) {
      externalOnChange({ target: { value: '' } });
    }
    navigate('/day');
  };

  // Map event type to style and icon
  const getEventStyle = event => {
    switch (event) {
      case 'creation':
        return { color: 'var(--color-blue)', icon: <FiPlus /> };
      case 'update':
        return { color: '#76b5c5', icon: <FiEdit /> };
      case 'deletion':
        return { color: '#e28743', icon: <FiTrash2 /> };
      default:
        return { color: 'var(--color-blue)', icon: <FiSmile /> };
    }
  };

  // Prefix for event types
  const getEventPrefix = event => {
    switch (event) {
      case 'creation':
        return t.search.createdPrefix;
      case 'update':
        return t.search.updatePrefix;
      case 'deletion':
        return t.search.deletionPrefix;
      default:
        return '';
    }
  };

  return (
    <div className="search-container" ref={searchRef}>
      <div className="search-bar">
        <FaSearch className="search-icon" />
        <input
          type="text"
          className="search-input"
          placeholder={t.search.placeholder}
          aria-label={t.search.ariaLabel}
          value={searchValue}
          onChange={handleInputChange}
        />
      </div>

      <div
        className="search-results"
        style={{ display: searchValue.trim() ? 'block' : 'none' }}
      >
        {filteredRoutes.map((route) => {
          const IconComponent = route.icon;
          const langTranslations = configTranslations[preferredLanguage] || configTranslations['nl'];
          const label = getTranslation(langTranslations, route.labelKey, route.labelKey);
          return (
            <div
              key={route.path}
              className="search-result-item"
              onClick={() => handleResultClick(route.path)}
            >
              {IconComponent && <IconComponent className="result-icon" />}
              <span className="result-label">{label}</span>
            </div>
          );
        })}

        {emailNotifications.map((n) => {
          const key = n._id || n.reservationId || n.creationTimestamp;
          const { color, icon } = getEventStyle(n.event);
          return (
            <div key={key} className="search-result-item email-notif">
              <span className="notif-icon" style={{ color }}>
                {icon}
              </span>
              <span className="notif-text">
                {n.guests}p - {n.date} om {n.time} <br />
                {getEventPrefix(n.event)} {n.firstName || ''} {n.lastName || ''}
              </span>
            </div>
          );
        })}

        <div
          className="search-result-item reservation-search-option"
          onClick={handleReservationSearchClick}
        >
          <FaCalendarAlt className="result-icon" />
          <span className="result-label">{t.search.searchInReservations}</span>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
